// TOAST_MAX — потолок тостов в памяти стора: тот же максимум, что кит держит на экране
// (NotifyToasts max=3, kit/v5/src/templates/notify.jsx) — хранить больше смысла нет.
const TOAST_MAX = 3;
// annFieldsFromParams — дублирует одноимённую функцию api/notifications.ts (та же форма
// buildFrameParams): импортировать её значением отсюда нельзя — api/notifications.ts тянет
// client.ts → i18n.ts → "@kit-v5", а notifyStore.ts обязан оставаться исполняемым под голым
// node:test (см. комментарий ProductDictReader в notify.ts про тот же разрыв).
function annFieldsFromParams(raw) {
    const out = {};
    if (!raw || typeof raw !== "object" || Array.isArray(raw))
        return out;
    const p = raw;
    if (typeof p.title === "string" && p.title)
        out.title = p.title;
    if (typeof p.kind === "string" && p.kind)
        out.kind = p.kind;
    if (p.tag && typeof p.tag === "object") {
        const t = p.tag;
        if (typeof t.label === "string" && typeof t.color === "string")
            out.tag = { label: t.label, color: t.color };
    }
    if (typeof p.required === "boolean")
        out.required = p.required;
    if (typeof p.confirmLabel === "string" && p.confirmLabel)
        out.confirmLabel = p.confirmLabel;
    if (typeof p.closesAt === "string")
        out.closesAt = p.closesAt;
    if (p.yesno && typeof p.yesno === "object") {
        const y = p.yesno;
        out.yesno = { yes: typeof y.yes === "string" ? y.yes : undefined, no: typeof y.no === "string" ? y.no : undefined };
    }
    if (p.poll && typeof p.poll === "object") {
        const pl = p.poll;
        const options = Array.isArray(pl.options)
            ? pl.options
                .filter((o) => !!o && typeof o === "object")
                .map((o) => ({ id: String(o.id ?? ""), label: String(o.label ?? "") }))
            : [];
        out.poll = {
            options,
            multi: !!pl.multi,
            anonymous: !!pl.anonymous,
            quiz: !!pl.quiz,
            correct: Array.isArray(pl.correct) ? pl.correct.map(String) : undefined,
        };
    }
    if (p.rating && typeof p.rating === "object") {
        const rt = p.rating;
        if (typeof rt.scale === "number") {
            out.rating = { scale: rt.scale, low: typeof rt.low === "string" ? rt.low : undefined, high: typeof rt.high === "string" ? rt.high : undefined };
        }
    }
    return out;
}
function paramsFromUnknown(raw) {
    const out = {};
    if (!raw || typeof raw !== "object" || Array.isArray(raw))
        return out;
    for (const [k, v] of Object.entries(raw)) {
        out[k] = typeof v === "number" ? v : String(v);
    }
    return out;
}
// parseLiveNotificationFrame — кадр общего канала (контракт кадра v2, shell main.go
// handleNotifyEvent): payload {id, source_slug, key, params, deep_link, unread}; source_slug
// читаем из payload, не из frame.product — payload несёт настоящего адресата карточки.
// Повреждённый/чужого типа кадр — null, не бросает.
export function parseLiveNotificationFrame(frame) {
    if (frame.type !== "notification")
        return null;
    const p = frame.payload;
    if (!p || typeof p !== "object")
        return null;
    const rec = p;
    const { id, key, deep_link: deepLink, unread, source_slug: sourceSlug } = rec;
    if (typeof id !== "string" || typeof key !== "string" || typeof deepLink !== "string" ||
        typeof unread !== "number" || typeof sourceSlug !== "string") {
        return null;
    }
    const item = {
        id,
        key,
        params: paramsFromUnknown(rec.params),
        deepLink,
        sourceSlug,
        createdAt: frame.ts,
        // ann_id кадра — camelCase нет: контракт кадра v2 несёт snake_case, тот же приём, что source_slug.
        annId: typeof rec.ann_id === "string" ? rec.ann_id : undefined,
        ...annFieldsFromParams(rec.params),
    };
    return { item, unread };
}
export class NotifyStore {
    api;
    state = { items: [], count: 0, toasts: [] };
    listeners = new Set();
    constructor(api) {
        this.api = api;
    }
    getState() {
        return this.state;
    }
    subscribe(cb) {
        this.listeners.add(cb);
        return () => { this.listeners.delete(cb); };
    }
    set(patch) {
        this.state = { ...this.state, ...patch };
        this.listeners.forEach((cb) => cb());
    }
    // load — снимок при входе/переустановке продукта «Уведомления»; сосед недоступен (503
    // notifications_unavailable) или любая другая сетевая ошибка — тихий пустой колокольчик,
    // без падения шапки (успех-критерий NOTIF-2B).
    async load() {
        try {
            const page = await this.api.fetchNotifications({ limit: 50 });
            this.set({ items: page.items, count: page.unread });
        }
        catch {
            this.set({ items: [], count: 0 });
        }
    }
    onLiveFrame(frame) {
        const parsed = parseLiveNotificationFrame(frame);
        if (!parsed)
            return;
        const items = [parsed.item, ...this.state.items.filter((i) => i.id !== parsed.item.id)];
        // Тосты: дедуп по id (повторный кадр той же строки не плодит второй тост) + потолок TOAST_MAX.
        const toasts = [...this.state.toasts.filter((t) => t.id !== parsed.item.id), parsed.item].slice(-TOAST_MAX);
        this.set({ items, count: parsed.unread, toasts });
    }
    // dismissToast — крестик/самоуход ТОЛЬКО тоста: уведомление остаётся в колокольчике (kit-jsdoc
    // NotifyToast), items/count не трогаем.
    dismissToast(id) {
        this.set({ toasts: this.state.toasts.filter((x) => x.id !== id) });
    }
    // seen — открытие ящика: счётчик гасится сразу (владелец: «гаснет при открытии»), ответ двери
    // несёт настоящий unread — им и подтверждаем (поля нет — перечитываем список).
    async seen() {
        this.set({ count: 0 });
        try {
            const res = await this.api.markNotificationsSeen();
            if (typeof res?.unread === "number")
                this.set({ count: res.unread });
            else
                await this.load();
        }
        catch {
            // провал — перечитываем список, чтобы экран не разошёлся с бэком молча
            await this.load();
        }
    }
    // clear — любой из равнозначных разборов строки/стопки (клик-переход, крестик, крестик
    // стопки): строка уезжает из ящика сразу, счётчик колокольчика — из ответа двери, не только
    // при закрытии ящика (иначе он старый до нового открытия).
    async clear(ids) {
        if (ids.length === 0)
            return;
        this.set({ items: this.state.items.filter((i) => !ids.includes(i.id)) });
        try {
            const res = await this.api.dismissNotifications(ids);
            if (typeof res?.unread === "number")
                this.set({ count: res.unread });
            else
                await this.load();
        }
        catch {
            // провал — перечитываем список (строка могла остаться на бэке)
            await this.load();
        }
    }
    // answer — ANN-V2 Ш6: ответ на объявление (annId — Draft.ID, не id строки колокольчика). Успех —
    // required снимается и строка перестаёт просить ответ (можно закрыть обычным крестиком), тост
    // с этим id убирается; провал (closed/locked/...) — items не трогаем (ответ не засчитан), только
    // перечитываем список, тем же приёмом, что clear()/seen() (ApiError.message уже локализован
    // словарём err.<code> в apiFetch — CLAUDE.md п.5, отдельного UI для текста ошибки здесь нет).
    async answer(itemId, annId, value) {
        try {
            await this.api.answerAnnouncement(annId, value && value.length ? value : ["confirm"]);
        }
        catch {
            await this.load();
            return;
        }
        const items = this.state.items.map((i) => (i.id === itemId ? { ...i, required: false, confirmLabel: undefined, poll: undefined, yesno: undefined, rating: undefined } : i));
        const toasts = this.state.toasts.filter((t) => t.id !== itemId);
        this.set({ items, toasts });
    }
    // clearAll — «Прочитать все»: тот же разбор, всей пачкой (all:true — не список id).
    async clearAll() {
        this.set({ items: [] });
        try {
            const res = await this.api.dismissNotifications({ all: true });
            if (typeof res?.unread === "number")
                this.set({ count: res.unread });
            else
                await this.load();
        }
        catch {
            // аналогично clear()
            await this.load();
        }
    }
}
